import type { DemoSourceMode } from '../../models/demoToneAnalysis'
import type { VideoPreview, VideoPreviewStatus } from '../../models/videoPreview'

type AnalysisSourceSelectorProps = {
  mode: DemoSourceMode
  youtubeUrl: string
  libraryArtist: string
  libraryTitle: string
  errors: {
    youtubeUrl?: string
    librarySearch?: string
  }
  preview: VideoPreview | null
  previewStatus: VideoPreviewStatus
  previewError: string
  disabled: boolean
  onModeChange: (mode: DemoSourceMode) => void
  onYoutubeUrlChange: (value: string) => void
  onLibraryArtistChange: (value: string) => void
  onLibraryTitleChange: (value: string) => void
  onRetryPreview: () => void
}

export function AnalysisSourceSelector({
  mode,
  youtubeUrl,
  libraryArtist,
  libraryTitle,
  errors,
  preview,
  previewStatus,
  previewError,
  disabled,
  onModeChange,
  onYoutubeUrlChange,
  onLibraryArtistChange,
  onLibraryTitleChange,
  onRetryPreview,
}: AnalysisSourceSelectorProps) {
  return <div className="ts-analysis-source">
    <div className="ts-analysis-source-tabs" role="tablist" aria-label="Analysis source">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'youtube'}
        className={mode === 'youtube' ? 'active' : ''}
        disabled={disabled}
        onClick={() => onModeChange('youtube')}
      >
        YouTube link
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'library'}
        className={mode === 'library' ? 'active' : ''}
        disabled={disabled}
        onClick={() => onModeChange('library')}
      >
        Search Tone Library
      </button>
    </div>

    {mode === 'youtube'
      ? <div className="ts-analysis-source-field">
          <label>
            <span>YouTube URL</span>
            <input
              value={youtubeUrl}
              onChange={(event) => onYoutubeUrlChange(event.target.value)}
              placeholder="https://www.youtube.com/watch?v=demo"
              disabled={disabled}
            />
          </label>
          {previewStatus === 'loading' && <div className="ts-video-preview-status" role="status" aria-live="polite">
            Loading YouTube preview...
          </div>}
          {previewStatus === 'loaded' && preview && <article className="ts-video-preview-card" aria-label={`${preview.title} preview`}>
            <img src={preview.thumbnailUrl} alt={`${preview.title} video thumbnail`} />
            <div>
              <span>{preview.provider}</span>
              <strong>{preview.title}</strong>
              <small>{preview.duration ? `${preview.channel} - ${preview.duration}` : preview.channel}</small>
            </div>
          </article>}
          {previewStatus === 'error' && <div className="ts-video-preview-error" role="alert">
            <span>{previewError}</span>
            <button type="button" disabled={disabled} onClick={onRetryPreview}>
              Retry preview
            </button>
          </div>}
          {errors.youtubeUrl && <small role="alert">{errors.youtubeUrl}</small>}
        </div>
      : <div className="ts-analysis-source-field">
          <div className="ts-library-search-grid">
            <label>
              <span>Artist</span>
              <input
                value={libraryArtist}
                onChange={(event) => onLibraryArtistChange(event.target.value)}
                placeholder="Artist name"
                disabled={disabled}
              />
            </label>
            <label>
              <span>Title</span>
              <input
                value={libraryTitle}
                onChange={(event) => onLibraryTitleChange(event.target.value)}
                placeholder="Song title"
                disabled={disabled}
              />
            </label>
          </div>
          {errors.librarySearch && <small role="alert">{errors.librarySearch}</small>}
        </div>}
  </div>
}
